import { inject, Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut } from '@angular/fire/auth';

import { FIREBASE_ERROR_MESSAGES } from '../../constants/constants';
import { SignInError } from './access.types';
import { FIREBASE_NOT_CONFIGURED, isFirebaseConfigured } from './firebase.providers';

const UNKNOWN_FIREBASE_MESSAGE = 'Unable to sign in right now. Please try again.';

// Firebase only checks the password; the backend issues the session tokens.
@Injectable({ providedIn: 'root' })
export class FirebaseSignInService {
  private readonly auth = inject(Auth);

  async idToken(email: string, password: string): Promise<string> {
    if (!isFirebaseConfigured()) {
      throw new SignInError(FIREBASE_NOT_CONFIGURED);
    }

    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      return await credential.user.getIdToken();
    } catch (error) {
      throw new SignInError(FIREBASE_ERROR_MESSAGES[errorCode(error)] ?? UNKNOWN_FIREBASE_MESSAGE);
    }
  }

  signOut(): void {
    if (!isFirebaseConfigured()) {
      return;
    }

    try {
      signOut(this.auth).catch(() => undefined);
    } catch {
      // already signed out
    }
  }
}

function errorCode(error: unknown): string {
  return typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof error.code === 'string'
    ? error.code
    : 'auth/unknown';
}
